import {Injectable} from '@angular/core';
import {Subject} from 'rxjs';
import Quill, {RangeStatic} from 'quill';
import {DropdownItem} from '@blocks/rich-text/dropdown-item';
import * as uuid from 'uuid';
import {BlockStore} from '@blocks/block/block.store';
import {BlockMutationService} from '@blocks/services/block-mutation/block-mutation.service';
import {take} from 'rxjs/operators';

export interface RichTextFormatCommand {
  id: string;
  blockId: number;
  kind: string;
  value?: any;
  selection?: RangeStatic;
}

export interface RichTextSetHeaderCommand {
  blockIds: number[];
  header: DropdownItem;
}

export interface RichTextCleanCommand {
  blockIds: number[];
  selection?: RangeStatic;
}

export interface RichTextInsertVariableCommand {
  blockId: number;
  name: string;
}

@Injectable()
export class RichTextService {
  constructor(private blockStore: BlockStore,
    private blockMutationService: BlockMutationService) { }

  focus$ = new Subject<number>();
  blur$ = new Subject<void>();
  undo$ = new Subject<number[]>();
  redo$ = new Subject<number[]>();
  clean$ = new Subject<RichTextCleanCommand>();
  setHeader$ = new Subject<RichTextSetHeaderCommand>();
  insertVariable$ = new Subject<RichTextInsertVariableCommand>();
  format$ = new Subject<RichTextFormatCommand>();
  formatStart$ = new Subject<number[]>();
  formatEnd$ = new Subject<number[]>();
  hideToolbar$ = new Subject<boolean>();

  format(blockIds: number[], kind: string, value?: any, selection?: RangeStatic) {
    this.formatStart$.next(blockIds);

    if (blockIds.length === 1) {
      this.format$.next({
        id: uuid.v4(),
        blockId: blockIds[0],
        kind,
        value,
        selection
      });
    } else if (blockIds.length > 1) {
      // When we format more than one rte, we do that with a shadow-quil-instance
      const div = document.createElement('div');
      const q = new Quill(div);
      const blocks = this.blockStore.getBlocksSync(blockIds);

      this.blockMutationService.begin(blocks[0].bucket_type, blocks[0].bucket_id);

      for (const block of blocks) {
        q.setContents(block.settings.delta, 'api');
        q.formatText(0, Number.MAX_SAFE_INTEGER, kind, value, 'api');

        block.settings.content = q.root.innerHTML;
        block.settings.delta = q.getContents();

        this.blockMutationService.addBlockUpdate(block);
      }

      this.blockMutationService.commit().pipe(take(1)).subscribe(x => this.formatEnd$.next(blockIds));
    }
  }

  focus(blockId: number) {
    this.focus$.next(blockId);
  }

  blur() {
    this.blur$.next();
  }

  undo(blockIds: number[]) {
    this.undo$.next(blockIds);
  }

  redo(blockIds: number[]) {
    this.redo$.next(blockIds);
  }

  clean(blockIds: number[], selection?: RangeStatic) {
    this.clean$.next({blockIds, selection});
  }

  setHeader(blockIds: number[], header: DropdownItem) {
    this.setHeader$.next({blockIds, header});
  }

  insertVariable(blockId: number, name: string) {
    this.insertVariable$.next({blockId, name});
  }

  hideToolbar() {
    this.hideToolbar$.next(true);
  }
}
